"use client";

/**
 * Guide-style trial offer: instead of selling Pro, it answers the easy
 * question ("can I try it free?") with a how-it-works timeline - today
 * everything unlocks, day 5 we warn you here, day 7 you drop back to
 * free. No card, no charge, one tap ("Start MY free trial" - ownership
 * wording). Shown only to signed-in free users who never started one
 * (user.pro_trial_eligible); dismissible.
 */
import { useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { captureEvent } from "@/lib/tracking";
import { useT } from "@/lib/locale-context";
import { API_BASE } from "@/lib/api";
import { Crown, Loader2, X } from "lucide-react";

const DISMISS_KEY = "menzil.trial.offer.dismissed";

/** ``embedded``: inside the signed-in hub, which owns width and spacing. */
export default function TrialOfferCard({ embedded = false }: { embedded?: boolean } = {}) {
  const t = useT();
  const { user, setUser, trialMode } = useAuth();
  const cardMode = (user?.trial_mode ?? trialMode) === "card";
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return typeof window !== "undefined" && localStorage.getItem(DISMISS_KEY) === "1"; }
    catch { return false; }
  });

  if (!user || !user.pro_trial_eligible || user.email_verified === false || dismissed) {
    return null;
  }

  async function start() {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/auth/trial/start`, {
        method: "POST",
        credentials: "include",
      });
      const d = await r.json().catch(() => null);
      if (d?.reason === "card_required" || cardMode) {
        // Card cohort: the trial starts on /pricing (plan choice +
        // card registration live there).
        const m = window.location.pathname.match(/^\/(az|ru|en)\b/);
        window.location.href = `/${m ? m[1] : "az"}/pricing`;
        return;
      }
      if (r.ok && d?.ok) {
        captureEvent("trial_started", { mode: "nocard" });
        // Refresh the user so the header chip + gates flip immediately.
        const me = await fetch(`${API_BASE}/auth/me`, { credentials: "include" })
          .then((x) => x.json()).catch(() => null);
        if (me?.user) setUser(me.user);
      }
    } finally {
      setBusy(false);
    }
  }

  const steps: { day: string; textKey: Parameters<typeof t>[0] }[] = [
    { day: t("trial.offer.day0.label"), textKey: "trial.offer.day0" },
    { day: t("trial.offer.day5.label"), textKey: cardMode ? "trial.offer.day5.card" : "trial.offer.day5" },
    { day: t("trial.offer.day7.label"), textKey: cardMode ? "trial.offer.day7.card" : "trial.offer.day7" },
  ];

  return (
    <section className={embedded ? "" : "max-w-3xl mx-auto px-4 sm:px-6 mt-6"}>
      <div className="relative rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
        <button
          type="button"
          onClick={() => {
            setDismissed(true);
            try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
          }}
          aria-label={t("onboard.dismiss")}
          className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 p-1"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-100 text-gray-700">
            <Crown className="w-4.5 h-4.5 w-[18px] h-[18px]" />
          </span>
          <h3 className="text-base font-bold text-gray-900">{t("trial.offer.title")}</h3>
        </div>

        {/* The how-it-works timeline - transparency IS the pitch. */}
        <ol className="mt-4 space-y-2.5">
          {steps.map((s, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className={`mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                i === 0 ? "bg-emerald-100 text-emerald-700" : i === 1 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"
              }`}>
                {s.day}
              </span>
              <p className="text-[13px] text-gray-700 leading-relaxed">{t(s.textKey).replace("{price}", "25")}</p>
            </li>
          ))}
        </ol>

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2.5">
          <button
            type="button"
            onClick={start}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-60 transition-colors"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("trial.offer.cta")}
          </button>
          <p className="text-[11px] text-gray-400">{cardMode ? t("trial.offer.note.card") : t("trial.offer.note")}</p>
        </div>
      </div>
    </section>
  );
}
